"use client"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Check } from "lucide-react"
import { useLanguage } from "@/lib/i18n/language-context"

export function ModelsSection() {
  const { t } = useLanguage()

  const models = [
    {
      name: t.musicAIName,
      description: t.musicAIDesc,
      features: [t.musicComposition, t.audioEnhancement, t.soundDesign],
      badge: t.mostPopular,
    },
    {
      name: t.photoAIName,
      description: t.photoAIDesc,
      features: [t.imageEnhancement, t.backgroundRemoval, t.styleTransfer],
    },
    {
      name: t.emailAIName,
      description: t.emailAIDesc,
      features: [t.emailGeneration, t.toneAdjustment, t.autoResponse],
    },
    {
      name: t.videoAIName,
      description: t.videoAIDesc,
      features: [t.videoEditing, t.sceneDetection, t.autoSubtitles],
    },
    {
      name: t.voiceAIName,
      description: t.voiceAIDesc,
      features: [t.voiceCloning, t.speechToText, t.textToSpeech],
    },
    {
      name: t.designAIName,
      description: t.designAIDesc,
      features: [t.logoGeneration, t.layoutSuggestions, t.colorPalettes],
    },
  ]

  return (
    <section id="models" className="bg-muted/30 px-4 py-20 md:px-6 md:py-32">
      <div className="container mx-auto">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-balance text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
            {t.modelsTitle}
          </h2>
          <p className="mx-auto max-w-2xl text-pretty text-lg text-muted-foreground">{t.modelsSubtitle}</p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {models.map((model) => (
            <Card key={model.name} className="relative border-border/50">
              {model.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">{model.badge}</Badge>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{model.name}</CardTitle>
                <CardDescription className="text-base">{model.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {model.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}
